import { type CountryCode, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js/max";
// The very file the library reads its own plans out of, so naming it here costs nothing: `libphonenumber-js/max`
// resolves to this same module, and a bundler that has already taken it in does not take it in twice.
import metadata from "libphonenumber-js/metadata.max";
import { FALLBACK_COUNTRY, localCountryCode } from "../../common/local-country";
import { fold, NO_MATCH, rankedFilter } from "../../common/option-search";

export type { CountryCode };

export interface Region {
  code: CountryCode;
  name: string;
  flag: string;
  // Digits alone, the plus belonging to how a number is written rather than to what the region's code is.
  callingCode: string;
}

// English, because the labels and the order they are sorted in are: a reader's own collation would put the list in an
// order the words it is reading do not explain.
const REGION_NAMES = new Intl.DisplayNames(["en"], { type: "region", fallback: "code" });

// A flag emoji is nothing but the region's own two letters written as regional indicators, so it is computed here
// rather than carried: /countries reads one off `world-countries` because it already holds that package, and 565 kB
// of names and borders would be a steep price for a picture this is three lines of arithmetic away from.
export function regionFlag(code: string): string {
  if (!TWO_LETTERS.test(code)) return "";
  const indicator = (letter: string) => String.fromCodePoint(FIRST_INDICATOR + letter.charCodeAt(0) - FIRST_LETTER);
  return [...code].map(indicator).join("");
}

const TWO_LETTERS = /^[A-Z]{2}$/;
const FIRST_INDICATOR = 0x1f1e6;
const FIRST_LETTER = "A".charCodeAt(0);

// What the library carries metadata for, which is the only list worth offering: a country it cannot parse against is
// one this page could read nothing for. It is 245 where /countries has 250, and it has Ascension and Tristan da Cunha
// as dialling regions of their own where ISO folds both into Saint Helena.
export const REGIONS: readonly Region[] = getCountries()
  .map((code) => ({
    code,
    name: REGION_NAMES.of(code) ?? code,
    flag: regionFlag(code),
    callingCode: getCountryCallingCode(code),
  }))
  .sort((a, b) => a.name.localeCompare(b.name, "en"));

const BY_CODE = new Map<string, Region>(REGIONS.map((region) => [region.code, region]));

// The region a calling code reaches when the digits after it have not yet said which of the countries sharing it is
// meant, which for the twenty-five on +1 is the United States. The order is the metadata's own — it lists the main
// country first, that being how the library itself answers a bare code — so no ranking is written here.
const MAIN_REGIONS = new Map<string, CountryCode>(
  Object.entries(metadata.country_calling_codes)
    .flatMap(([calling, countries]) => countries[0] ? [[calling, countries[0]] as [string, CountryCode]] : []),
);

// The country a calling code belongs to, and nothing at all for the global codes: +800 belongs to no country, which
// is why the metadata keeps those in a list of their own and this map never holds one.
export function mainRegion(callingCode: string): CountryCode | undefined {
  return MAIN_REGIONS.get(callingCode);
}

// The label is the name alone, per **The Flag Goes Beside the Label and Never Inside It**: a searchable Select seeds
// its own search box with the label of what is selected, and two regional indicators in there match no region at all.
export const REGION_OPTIONS = REGIONS.map((region) => ({ value: region.code, label: region.name }));

// A share link carries anything at all, so a code out of one is looked up rather than trusted.
export function findRegion(code: unknown): Region | undefined {
  return typeof code === "string" ? BY_CODE.get(code.toUpperCase()) : undefined;
}

// Total by construction, which is what opens the page on a link naming a region the library has since dropped.
export function pickRegion(code: unknown): Region {
  return findRegion(code) ?? findRegion(localCountryCode()) ?? findRegion(FALLBACK_COUNTRY) ?? REGIONS[0];
}

// Where the reader is, which is a different question from the region a number is read against: a link shared out of
// Berlin opens on a German number wherever it is followed, and how to dial it from there is the follower's own.
export const HOME_REGION = pickRegion(undefined);

// A leading plus is the number naming its own country, and the picker follows it rather than the other way about.
// Without one the digits are national and mean whatever the region already chosen says they mean.
export function regionForInput(input: string, current: CountryCode): CountryCode | undefined {
  if (!input.trimStart().startsWith("+")) return undefined;
  // A number complete enough to parse names one of the several regions its calling code reaches: +1 416 is Canada
  // where +1 202 is the United States, and only the digits after the code tell the twenty-five of them apart.
  const parsed = parsePhoneNumberFromString(input)?.country;
  if (parsed) return parsed;

  const calling = callingCodePrefix(input);
  if (!calling) return undefined;
  // Half a number says no more than its calling code, and the reader has already picked among the regions sharing
  // one: typing +1 with Canada showing must not answer with the United States.
  return findRegion(current)?.callingCode === calling ? current : MAIN_REGIONS.get(calling);
}

// Picking a country while the box holds an international number rewrites the code it opens with, since the plus is
// what decides and a flag disagreeing with the digits beside it is a picker that has stopped meaning anything.
// Everything past the code is kept exactly as it was typed, spacing and all: only the code itself moves.
export function withRegion(input: string, region: Region): string {
  const trimmed = input.trimStart();
  if (!trimmed.startsWith("+")) return input;

  const calling = callingCodePrefix(trimmed);
  if (!calling) return `+${region.callingCode}`;

  const after = trimmed.slice(1);
  let digits = 0;
  let at = 0;
  while (at < after.length && digits < calling.length) {
    if (DIGIT.test(after[at])) digits++;
    at++;
  }
  return `+${region.callingCode}${after.slice(at)}`;
}

// The longest calling code the digits open with, 1, 12 and 123 all being codes and the longest the one meant.
function callingCodePrefix(input: string): string | undefined {
  const digits = input.replace(DIGITS, "");
  for (let length = MAX_CALLING_CODE_DIGITS; length > 0; length--) {
    const prefix = digits.slice(0, length);
    if (MAIN_REGIONS.has(prefix)) return prefix;
  }
  return undefined;
}

const DIGIT = /\d/;
const DIGITS = /\D/g;
const MAX_CALLING_CODE_DIGITS = 3;

// Ranked rather than left alphabetical, for the reason `countryFilter` is: "in" is inside a third of the names here,
// and India, Indonesia and the twenty countries merely containing the letters must not arrive in one undifferentiated
// list. A code or a dialling code typed in full is the one thing somebody cannot have meant loosely.
export const regionFilter = rankedFilter(rankOf, (search) => fold(search.replace(/^\+/, "")));

// Not the bands `termRank` holds: a dialling code is reached by its start rather than by being anywhere in the text.
function rankOf(code: string, needle: string): number {
  const terms = SEARCH_TERMS.get(code);
  if (!terms) return NO_MATCH;
  if (terms.code === needle || terms.calling === needle || terms.reserved === needle) return 0;
  if (terms.name.startsWith(needle)) return 1;
  if (terms.name.includes(needle)) return 2;
  return terms.calling.startsWith(needle) ? 3 : NO_MATCH;
}

interface RegionTerms {
  name: string;
  code: string;
  calling: string;
  reserved?: string;
}

// ISO 3166 reserves UK for the United Kingdom at its request, and it is what most people type for it. The other codes
// it reserves name a region this list carries under a code of its own, or one that dials as part of a bigger one.
const RESERVED_CODES: Record<string, string> = { GB: "uk" };

const SEARCH_TERMS = new Map<string, RegionTerms>(
  REGIONS.map((region) => [region.code, {
    name: fold(region.name),
    code: fold(region.code),
    calling: region.callingCode,
    reserved: RESERVED_CODES[region.code],
  }]),
);
